import { useCallback, useMemo, useRef, useState } from 'react'
import { ToastContext } from './toastContext'
import ResultDialog from './ResultDialog'

/**
 * Every outcome in the app, on one queue.
 *
 * This used to raise a corner toast that drained away after four seconds. The
 * legacy raises a centred SweetAlert instead — 355 `Swal.fire` calls across the
 * MVC views, one for every write — and it holds the screen until the person who
 * asked for the write has pressed `إغلاق`. That is the behaviour kept here: an
 * answer you can miss is not an answer, and a corner card that leaves on its own
 * is missable by design.
 *
 * The API did not change. `toast.success(...)` still reads the same at all
 * thirty-odd call sites; only what it raises did — which is why the switch is
 * one file rather than thirty.
 *
 * Outcomes QUEUE rather than stack. Two dialogs cannot share a screen the way
 * two toasts could share a corner, so a second answer waits for the first to be
 * dismissed instead of replacing it unread.
 */
export function ToastProvider({ children }) {
  const [queue, setQueue] = useState([])
  const nextId = useRef(0)

  const dismiss = useCallback(() => setQueue((previous) => previous.slice(1)), [])

  const push = useCallback(({ tone = 'info', message }) => {
    if (!message) return
    const id = (nextId.current += 1)
    setQueue((previous) => [...previous, { id, tone, message }])
  }, [])

  const value = useMemo(
    () => ({
      push,
      success: (message) => push({ tone: 'success', message }),
      error: (message) => push({ tone: 'danger', message }),
      info: (message) => push({ tone: 'info', message }),
    }),
    [push],
  )

  const current = queue[0] ?? null

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* NOT keyed. A `key` here remounted the dialog every time the queue
          moved, which tore the node off before its exit animation could run —
          re-running the mark's draw for a second outcome is handled inside
          ResultDialog instead, where it does not cost the exit. */}
      <ResultDialog
        result={current && { result: current.tone === 'success', message: current.message }}
        tone={current?.tone}
        onClose={dismiss}
      />
    </ToastContext.Provider>
  )
}
